<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, setStoredLocale, type Locale } from '@/i18n'
import { accountHref, swapLocaleInPath } from '@/router/products'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { isAuthenticated } = useAuth()

const current = computed<Locale>(() => route.meta.locale ?? DEFAULT_LOCALE)
const otherLocales = computed(() => SUPPORTED_LOCALES.filter((l) => l !== current.value))

const isOpen = ref(false)
const rootEl = ref<HTMLElement | null>(null)

/** « Contacts et aide » : connecté → formulaire interne ; sinon → externe. */
const mounted = ref(false)
const EXTERNAL_CONTACT = 'https://smerra.fr/contacts/'
const contactInternal = computed(() => mounted.value && isAuthenticated.value)
const contactHref = computed(() =>
  contactInternal.value ? accountHref('contact', current.value) : EXTERNAL_CONTACT,
)

const localeName = (l: Locale) => t('locale.name', l, { locale: l })

function toggle() { isOpen.value = !isOpen.value }
function close() { isOpen.value = false }

function changeLocale(target: Locale) {
  // Fixe la préférence avant de naviguer (sinon la garde router, lisant l'ancienne
  // préférence, ré-aligne aussitôt sur la langue précédente).
  setStoredLocale(target)
  // `replace` : changer de langue ne doit pas empiler une entrée d'historique
  // (sinon « Précédent » ré-affiche juste la même étape dans l'autre langue).
  router.replace(swapLocaleInPath(route.path, target))
  close()
}

function onDocClick(e: MouseEvent) {
  if (!isOpen.value || !rootEl.value) return
  if (!rootEl.value.contains(e.target as Node)) isOpen.value = false
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') isOpen.value = false
}

onMounted(() => {
  mounted.value = true
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKey)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKey)
})
</script>

<template>
  <div class="topbar" ref="rootEl">
    <div class="topbar__inner">
      <nav class="topbar__links" aria-label="Menu utilitaire">
        <ul>
          <li>
            <router-link v-if="contactInternal" :to="contactHref">{{ t('topbar.contact') }}</router-link>
            <a v-else :href="contactHref" target="_blank" rel="noopener">{{ t('topbar.contact') }}</a>
          </li>
        </ul>
      </nav>

      <div class="topbar__lang">
        <button
          type="button"
          class="topbar__lang-trigger"
          :aria-expanded="isOpen"
          :aria-label="t('topbar.switchLabel')"
          @click.stop="toggle"
        >
          <abbr :title="localeName(current)" :lang="current">{{ current }}</abbr>
          <svg
            class="topbar__chevron"
            :class="{ 'topbar__chevron--open': isOpen }"
            viewBox="0 0 16 16"
            width="14"
            height="14"
            aria-hidden="true"
          >
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <ul v-if="isOpen" class="topbar__lang-menu" role="menu">
          <li v-for="l in otherLocales" :key="l" role="none">
            <button
              type="button"
              role="menuitem"
              :hreflang="l"
              :lang="l"
              @click="changeLocale(l)"
            >
              {{ localeName(l) }}
            </button>
          </li>
          <li role="none" aria-disabled="true">
            <span :lang="current">{{ localeName(current) }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
.topbar {
  display: none;
  background: var(--color-beige);
}
@media (min-width: 64rem) {
  .topbar {
    display: block;
    position: sticky;
    top: 0;
    z-index: var(--z-topbar);
  }
}

.topbar__inner {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.875rem;
  padding-inline: 1.5rem;
  min-height: var(--topbar-h);
  font-size: var(--text-xs);
  color: var(--color-chocolate);
}

.topbar__links ul {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding-block: 0.125rem;
}
.topbar__links a {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.25rem 0.375rem 0.375rem;
  font-weight: 500;
  color: var(--color-chocolate);
  transition: color var(--dur-fast) var(--ease-out);
}
.topbar__links a:hover { color: var(--color-cherry); }

.topbar__lang { position: relative; }
.topbar__lang-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  padding: 0.375rem;
  color: var(--color-chocolate);
}
.topbar__lang-trigger abbr {
  text-decoration: none;
  font-weight: 500;
  text-transform: capitalize;
  font-size: var(--text-xs);
}
.topbar__chevron { transition: transform var(--dur-fast) var(--ease-out); }
.topbar__chevron--open { transform: rotate(180deg); }

.topbar__lang-menu {
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  min-width: 8rem;
  padding-block: 0.375rem;
  background: var(--color-white);
  border-radius: 0.4rem;
  box-shadow: var(--shadow-md);
  z-index: var(--z-header);
}
.topbar__lang-menu li > button,
.topbar__lang-menu li > span {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.25rem 0.625rem;
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--color-chocolate);
  transition: color var(--dur-fast) var(--ease-out);
}
.topbar__lang-menu li > button { cursor: pointer; }
.topbar__lang-menu li > button:hover { color: var(--color-cherry); }
.topbar__lang-menu li[aria-disabled="true"] > span {
  color: var(--text-muted);
  cursor: default;
}
</style>
